"use client"

import { useEffect, useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import {
  SHAPE_OFFSETS,
  GRID_SIZE,
  getDefaultShape,
  getShapeDimensions,
  getSizeTier,
  type ShapeName,
} from "@/lib/grid-logic"
import { createClient } from "@/lib/supabase/client"
import { SkillArtIcon } from "@/components/skill-art-icon"

export interface GridLayoutTile {
  slug: string
  x: number
  y: number
  shape: string
}

type DisplayTile = {
  slug: string
  x: number
  y: number
  shape: ShapeName
}

function normalizeShape(shape: string | undefined): ShapeName {
  if (shape && shape in SHAPE_OFFSETS) {
    return shape as ShapeName
  }
  return "1x1"
}

function layoutToTiles(layout: GridLayoutTile[]): DisplayTile[] {
  return layout.map((tile) => ({
    slug: tile.slug,
    x: tile.x,
    y: tile.y,
    shape: normalizeShape(tile.shape),
  }))
}

function packToTiles(skills: string[]): DisplayTile[] {
  const tiles: DisplayTile[] = []
  let i = 0
  for (let y = 0; y < GRID_SIZE && i < skills.length; y++) {
    for (let x = 0; x < GRID_SIZE && i < skills.length; x++) {
      tiles.push({ slug: skills[i], x, y, shape: "1x1" })
      i += 1
    }
  }
  return tiles
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SkillGridProps {
  skills: string[]
  gridLayout?: GridLayoutTile[]
  size?: "sm" | "lg"
  className?: string
  emptyMessage?: string
  previewVariant?: "default" | "detail"
  initialSkillInfo?: Map<
    string,
    {
      name: string
      description: string
      category?: string
      skillMdSize?: number | null
    }
  >
}

export function SkillGrid({
  skills,
  gridLayout,
  size = "sm",
  className,
  emptyMessage = "No skills attached",
  previewVariant = "default",
  initialSkillInfo,
}: SkillGridProps) {
  const gridPx = size === "lg" ? "200px" : "125px"
  const tiles = gridLayout?.length ? layoutToTiles(gridLayout) : packToTiles(skills)
  const isEmpty = skills.length === 0

  const [hovered, setHovered] = useState<string | null>(null)
  const [pinned, setPinned] = useState<string | null>(null)
  const [fetchedSkillInfo, setFetchedSkillInfo] = useState<
    Map<
      string,
      {
        name: string
        description: string
        category?: string
        skillMdSize?: number | null
      }
    >
  >(new Map())

  // Stable dep: avoids refetching when parent passes a new array reference
  // with the same slugs. Also lets us short-circuit when the parent already
  // supplied complete initialSkillInfo.
  const slugsKey = useMemo(() => skills.join(","), [skills])
  const hasCompleteInitialInfo = useMemo(() => {
    if (!initialSkillInfo || initialSkillInfo.size === 0) return false
    for (const slug of skills) {
      if (!initialSkillInfo.has(slug)) return false
    }
    return true
  }, [initialSkillInfo, slugsKey])

  const skillInfo =
    initialSkillInfo && initialSkillInfo.size > 0
      ? initialSkillInfo
      : fetchedSkillInfo

  const activeSlug = hovered ?? pinned
  const isDetailPreview = previewVariant === "detail"

  // Fetch skill info from DB only when parent didn't already provide it.
  // Aborting on unmount prevents orphaned gotrue auth-token locks, which
  // otherwise hold the Supabase auth lock for 5s after navigation and
  // stall the next page render.
  useEffect(() => {
    if (skills.length === 0) return
    if (hasCompleteInitialInfo) return

    const controller = new AbortController()
    let cancelled = false

    async function fetchInfo() {
      const supabase = createClient()
      const { data } = await supabase
        .from("skills")
        .select("slug, name, description, category, skill_md_size")
        .in("slug", skills)
        .abortSignal(controller.signal)

      if (cancelled || !data) return

      const map = new Map<
        string,
        {
          name: string
          description: string
          category?: string
          skillMdSize?: number | null
        }
      >()
      for (const s of data) {
        map.set(s.slug, {
          name: s.name,
          description: s.description ?? "",
          category: s.category ?? undefined,
          skillMdSize: s.skill_md_size ?? null,
        })
      }
      setFetchedSkillInfo(map)
    }

    fetchInfo().catch(() => {
      // Aborts and transient failures are safe to ignore — the parent will
      // either re-render with initialSkillInfo or the user will navigate.
    })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [slugsKey, hasCompleteInitialInfo])

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <div>
        <div
          className="relative grid border border-muted-foreground/30"
          style={{
            gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
            gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
            width: gridPx,
            height: gridPx,
          }}
        >
          {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, i) => {
            const col = (i % GRID_SIZE) + 1
            const row = Math.floor(i / GRID_SIZE) + 1
            return (
              <div
                key={`bg-${i}`}
                className="border border-muted-foreground/20 bg-background"
                style={{ gridColumn: col, gridRow: row }}
              />
            )
          })}
          {tiles.map((tile) => {
            const info = skillInfo.get(tile.slug)
            const shape =
              tile.shape ??
              getDefaultShape(getSizeTier(info?.skillMdSize ?? 0))
            const { cols, rows } = getShapeDimensions(shape)
            const isActive = tile.slug === activeSlug
            const isPinned = tile.slug === pinned

            return (
              <button
                key={`${tile.slug}-${tile.x}-${tile.y}-${shape}`}
                type="button"
                aria-label={info?.name ?? tile.slug}
                className="relative z-10 overflow-hidden border bg-transparent transition-colors"
                style={{
                  gridColumn: `${tile.x + 1} / span ${cols}`,
                  gridRow: `${tile.y + 1} / span ${rows}`,
                  borderColor: isPinned
                    ? "rgba(59,130,246,0.9)"
                    : isActive
                      ? "rgba(59,130,246,0.6)"
                      : "transparent",
                  boxShadow: isPinned
                    ? "0 0 0 1px rgba(59,130,246,0.55)"
                    : undefined,
                }}
                onMouseEnter={() => setHovered(tile.slug)}
                onMouseLeave={() => setHovered(null)}
                onClick={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  setPinned(pinned === tile.slug ? null : tile.slug)
                }}
              >
                <SkillArtIcon
                  skillId={tile.slug}
                  category={info?.category}
                  skillMdSize={info?.skillMdSize}
                  shape={shape}
                  label={info?.name ?? tile.slug}
                  className="h-full w-full border-0 bg-transparent"
                />
              </button>
            )
          })}
        </div>
      </div>

      {/* Info panel — right side */}
      {isEmpty ? (
        <div className="min-w-0 flex-1 py-2">
          <p className={cn(isDetailPreview ? "text-sm" : "text-xs", "text-muted-foreground/60 italic")}>
            {emptyMessage}
          </p>
        </div>
      ) : activeSlug ? (() => {
        const info = skillInfo.get(activeSlug)
        const description = info?.description?.trim()
        return (
          <div className="min-w-0 flex-1 py-2">
            <p className={cn("font-mono font-semibold text-foreground", isDetailPreview ? "text-sm" : "text-xs")}>
              {activeSlug}
            </p>
            {description ? (
              <p className={cn("mt-2 line-clamp-3 leading-relaxed text-muted-foreground", isDetailPreview ? "text-sm" : "text-[11px]")}>
                {description}
              </p>
            ) : (
              <p className={cn("mt-2 leading-relaxed text-muted-foreground/60 italic", isDetailPreview ? "text-sm" : "text-[11px]")}>
                Loading description…
              </p>
            )}
          </div>
        )
      })() : null}
    </div>
  )
}
